import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Droplets, Wind, ThermometerSun, Footprints, ChevronDown, MapPin } from "lucide-react";
import { useT } from "../context/LanguageContext";
import { landingT } from "../translations/ui";
import LanguageSwitcher from "../components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ASSETS = {
  logo: "/logo.png",
  boot1: "/boot1.jpg",
  boot2: "/boot2.jpg",
  coach: "/coach.jpg",
  portugal: "/portugal.jpg",
};

// Custom hook for scroll reveal animations
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Hero Section
const HeroSection = ({ onScrollToWaitlist, t }) => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.muted = true;
      video.play().catch(() => {});
    };

    video.addEventListener('canplay', () => {
      setVideoLoaded(true);
      tryPlay();
    });

    video.addEventListener('loadeddata', () => {
      setVideoLoaded(true);
      tryPlay();
    });

    if (video.readyState >= 2) {
      setVideoLoaded(true);
      tryPlay();
    }

    return () => {
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('loadeddata', tryPlay);
    };
  }, []);

  return (
    <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
      <div className="hero-video-container">
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay loop muted playsInline preload="auto"
          className="hero-video"
          src="/hero-video.mp4"
          style={{ opacity: videoLoaded ? 1 : 0, transition: 'opacity 1s ease' }}
        />
        <div className="hero-overlay" />
      </div>

      <div className="relative z-10 h-full flex flex-col">
        <header className="flex justify-end items-center gap-3 p-6 md:p-12">
          <LanguageSwitcher />
          <a href="/blog" data-testid="hero-blog-link" className="btn-primary px-6 py-3 text-sm no-underline">
            {t.blog}
          </a>
        </header>

        <div className="flex-1 flex flex-col justify-center px-6 md:px-12 max-w-4xl">
          <p className="label-text text-white/60 mb-4 animate-fade-in-up">
            {t.heroSubtitle}
          </p>
          <h1 data-testid="hero-headline" className="heading-xl text-4xl sm:text-5xl lg:text-7xl text-white mb-6 animate-fade-in-up animation-delay-100">
            {t.heroTitle1}<br />{t.heroTitle2}
          </h1>
          <p className="body-text text-white/70 text-base md:text-lg max-w-xl mb-10 animate-fade-in-up animation-delay-200">
            {t.heroDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
            <button data-testid="hero-cta" onClick={onScrollToWaitlist} className="btn-primary w-fit">
              {t.heroCta}
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-white/40" />
        </div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = ({ t }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section data-testid="problem-section" ref={ref} className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className={`section-container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={ASSETS.coach} alt="Football coach on the sideline" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
          </div>
          <div>
            <p className="label-text text-white/40 mb-4">{t.problemLabel}</p>
            <h2 className="heading-xl text-3xl md:text-4xl lg:text-5xl text-white mb-8">{t.problemTitle}</h2>
            <div className="space-y-6 body-text text-white/60 text-base">
              <p>{t.problemP1}</p>
              <p>{t.problemP2}</p>
              <p className="text-white/80 italic">{t.problemP3}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = ({ t }) => {
  const [ref, isVisible] = useScrollReveal();

  const features = [
    { icon: <Footprints className="w-6 h-6" />, title: t.feat1Title, description: t.feat1Desc },
    { icon: <Droplets className="w-6 h-6" />, title: t.feat2Title, description: t.feat2Desc },
    { icon: <ThermometerSun className="w-6 h-6" />, title: t.feat3Title, description: t.feat3Desc },
    { icon: <Wind className="w-6 h-6" />, title: t.feat4Title, description: t.feat4Desc },
  ];

  return (
    <section data-testid="solution-section" ref={ref} className="py-24 md:py-32 bg-black">
      <div className={`section-container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="text-center mb-16">
          <p className="label-text text-white/40 mb-4">{t.solutionLabel}</p>
          <h2 className="heading-xl text-3xl md:text-4xl lg:text-5xl text-white mb-6">{t.solutionTitle}</h2>
          <p className="body-text text-white/50 max-w-xl mx-auto">{t.solutionDesc}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {ASSETS.boot1 && (
            <div className="aspect-[4/3] overflow-hidden">
              <img src={ASSETS.boot1} alt="The Gaffer boot detail" className="w-full h-full object-cover" />
            </div>
          )}
          {ASSETS.boot2 && (
            <div className="aspect-[4/3] overflow-hidden">
              <img src={ASSETS.boot2} alt="The Gaffer boot profile" className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="feature-card group" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="feature-icon mb-4">{feature.icon}</div>
              <h3 className="heading-md text-lg text-white mb-2">{feature.title}</h3>
              <p className="body-text text-white/50 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quality Section
const QualitySection = ({ t }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section data-testid="quality-section" ref={ref} className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className={`section-container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="label-text text-white/40 mb-4">{t.qualityLabel}</p>
            <h2 className="heading-xl text-3xl md:text-4xl lg:text-5xl text-white mb-8">{t.qualityTitle}</h2>
            <div className="space-y-6 body-text text-white/60 text-base">
              <p>{t.qualityP1}</p>
              <p>{t.qualityP2}</p>
            </div>
            <div className="mt-8 inline-flex items-center gap-3 border border-white/10 px-6 py-3">
              <MapPin className="w-5 h-5 text-white/60" />
              <span className="label-text text-white/60">{t.qualityBadge}</span>
            </div>
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img src={ASSETS.portugal} alt="Portuguese craftsmanship" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer / Lead Capture Section
const FooterSection = ({ waitlistRef, t }) => {
  const [ref, isVisible] = useScrollReveal();
  const [fname, setFname] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const mailchimpFormRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: fname }),
      });
      } catch (err) {
        console.error("Waitlist backend save failed:", err);
      }

    if (mailchimpFormRef.current) {
      mailchimpFormRef.current.submit();
    }

    setSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <section 
      data-testid="footer-section"
      ref={(el) => {
        ref.current = el;
        waitlistRef.current = el;
      }}
      className="py-24 md:py-32 bg-black"
    >
      <div className={`section-container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="heading-xl text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            {t.footerTitle}
          </h2>
          <p className="body-text text-white/50 mb-12">
            {t.footerDesc}
          </p>

          {submitted ? (
            <div data-testid="waitlist-success" className="border border-white/20 p-8">
              <p className="heading-md text-xl text-white mb-2">{t.footerSuccessTitle}</p>
              <p className="text-white/50 text-sm">{t.footerSuccessDesc}</p>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input data-testid="fname-input" type="text" value={fname} onChange={(e) => setFname(e.target.value)} placeholder={t.footerFname} className="input-minimal sm:w-1/3" />
                  <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.footerEmail} className="input-minimal sm:w-2/3" required />
                </div>
                <button data-testid="submit-button" type="submit" disabled={isSubmitting} className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap sm:w-auto">
                  {isSubmitting ? t.footerSubmitting : t.footerCta}
                </button>
              </form>

              <iframe name="mc-hidden-frame" style={{ display: 'none' }} title="mailchimp" />
              <form ref={mailchimpFormRef} action="https://gafferathletics.us8.list-manage.com/subscribe/post?u=cc56a6453847e2737c12fd02a&amp;id=e5455c0d65&amp;f_id=00ce1be1f0" method="post" target="mc-hidden-frame" style={{ display: 'none' }}>
                <input type="text" name="FNAME" value={fname} readOnly />
                <input type="email" name="EMAIL" value={email} readOnly />
                <input type="text" name="b_cc56a6453847e2737c12fd02a_e5455c0d65" tabIndex="-1" defaultValue="" />
              </form>
            </>
          )}
        </div>

        {/* Company Info */}
        <div className="mt-24 pt-12 border-t border-white/5">
          <div className="text-center mb-8">
            <p className="text-white/30 text-xs leading-relaxed">{t.footerCompany}</p>
            <p className="text-white/30 text-xs mt-1">{t.footerEmailAddr}</p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <a href="/terms" className="text-white/30 hover:text-white/50 text-xs transition-colors">{t.footerLinks.terms}</a>
            <a href="/privacy" className="text-white/30 hover:text-white/50 text-xs transition-colors">{t.footerLinks.privacy}</a>
            <a href="/accessibility" className="text-white/30 hover:text-white/50 text-xs transition-colors">{t.footerLinks.accessibility}</a>
            <a href="/complaints" className="text-white/30 hover:text-white/50 text-xs transition-colors">{t.footerLinks.complaints}</a>
            <a href="/withdrawal" className="text-white/30 hover:text-white/50 text-xs transition-colors">{t.footerLinks.withdrawal}</a>
          </div>

          <div className="text-center">
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-6 mx-auto mb-4 opacity-30" />
            <p className="text-white/20 text-xs">{t.footerCopyright}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Landing Page
export default function LandingPage() {
  const t = useT(landingT);
  const waitlistRef = useRef(null);

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main>
      <HeroSection onScrollToWaitlist={scrollToWaitlist} t={t} />
      <ProblemSection t={t} />
      <SolutionSection t={t} />
      <QualitySection t={t} />
      <FooterSection waitlistRef={waitlistRef} t={t} />
    </main>
  );
}
